
import React, { useEffect, useRef } from "react";
import { Tables } from "@/integrations/supabase/types";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import NoReportsMessage from "./states/NoReportsMessage";

type CrimeReport = Tables<"crime_reports">;

interface FallbackLeafletMapProps {
  crimeReports: CrimeReport[];
  isRecent: (date: string) => boolean;
  onLocationSelect?: (location: { lat: number; lng: number; name?: string }) => void;
  initialLocation?: { lat: number; lng: number; name?: string } | null;
}

const FallbackLeafletMap: React.FC<FallbackLeafletMapProps> = ({
  crimeReports,
  isRecent,
  onLocationSelect,
  initialLocation
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Initialize the map if it doesn't exist yet
    if (!mapRef.current) {
      // Default center (London)
      const defaultCenter: [number, number] = [51.505, -0.09];
      const center = initialLocation 
        ? [initialLocation.lat, initialLocation.lng] as [number, number]
        : defaultCenter;

      mapRef.current = L.map(mapContainerRef.current).setView(center, 13);

      // Add OpenStreetMap tiles
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(mapRef.current);

      // Add click handler for location selection
      if (onLocationSelect) {
        mapRef.current.on("click", function(e) {
          const { lat, lng } = e.latlng;
          
          // Remove existing marker if any
          if (userMarkerRef.current) {
            userMarkerRef.current.remove();
          }
          
          // Create a new marker at the clicked position
          userMarkerRef.current = L.marker([lat, lng], {
            draggable: true,
            title: "Drag to adjust location"
          }).addTo(mapRef.current!);
          
          // Add dragend event to the marker
          userMarkerRef.current.on('dragend', function() {
            const newPosition = userMarkerRef.current!.getLatLng();
            onLocationSelect({
              lat: newPosition.lat,
              lng: newPosition.lng,
              name: `Location (${newPosition.lat.toFixed(4)}, ${newPosition.lng.toFixed(4)})`
            });
          });
          
          onLocationSelect({
            lat,
            lng,
            name: `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`
          });
        });
      }
    }

    // If we have an initial location, update the map and add a marker
    if (initialLocation && mapRef.current) {
      mapRef.current.setView([initialLocation.lat, initialLocation.lng], 14);
      
      // Remove existing marker if any
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
      }
      
      // Create a new marker at the initial location
      userMarkerRef.current = L.marker([initialLocation.lat, initialLocation.lng], {
        draggable: true,
        title: "Drag to adjust location"
      }).addTo(mapRef.current);
      
      // Add dragend event to the marker
      if (onLocationSelect) {
        userMarkerRef.current.on('dragend', function() {
          const newPosition = userMarkerRef.current!.getLatLng();
          onLocationSelect({
            lat: newPosition.lat,
            lng: newPosition.lng,
            name: `Location (${newPosition.lat.toFixed(4)}, ${newPosition.lng.toFixed(4)})`
          });
        });
      }
    }

    // Add crime report markers only if this is a crime report map
    if (crimeReports.length > 0 && mapRef.current) {
      // Create markers for each crime report
      const markers: L.Marker[] = [];
      
      crimeReports.forEach((report) => {
        const recentClass = isRecent(report.created_at) ? "recent" : "";
        
        // Create a custom icon based on the crime category
        const icon = L.divIcon({
          className: `crime-marker ${report.category} ${recentClass}`,
          html: `<div class="marker-pin"></div>`,
          iconSize: [30, 42],
          iconAnchor: [15, 42]
        });
        
        const marker = L.marker([report.latitude, report.longitude], { icon })
          .addTo(mapRef.current!);
          
        // Add popup with crime details
        marker.bindPopup(`
          <div class="crime-popup">
            <h3>${report.category}</h3>
            <p><strong>Date:</strong> ${new Date(report.created_at).toLocaleString()}</p>
            <p><strong>Location:</strong> ${report.location_name || 'Unknown'}</p>
            ${report.description ? `<p>${report.description}</p>` : ''}
          </div>
        `);
        
        markers.push(marker);
      });
      
      // Fit the map to show all markers if there are any
      if (markers.length > 0) {
        const group = L.featureGroup(markers);
        mapRef.current.fitBounds(group.getBounds(), {
          padding: [50, 50]
        });
      }
    }

    // Clean up map when component unmounts
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [crimeReports, isRecent, initialLocation, onLocationSelect]);

  // Add CSS for the markers
  useEffect(() => {
    // Add the necessary styles for the markers and popups
    const style = document.createElement('style');
    style.innerHTML = `
      .crime-marker {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .marker-pin {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: #ff4757;
        border: 3px solid white;
        box-shadow: 0 0 5px rgba(0,0,0,0.3);
      }
      .crime-marker.recent .marker-pin {
        background-color: #ff6b81;
        animation: pulse 1.5s infinite;
      }
      .crime-marker.Theft .marker-pin { background-color: #ffa502; }
      .crime-marker.Assault .marker-pin { background-color: #ff4757; }
      .crime-marker.Burglary .marker-pin { background-color: #2ed573; }
      .crime-marker.Vandalism .marker-pin { background-color: #1e90ff; }
      .crime-marker.Homicide .marker-pin { background-color: #7d5fff; }
      .crime-marker.Other .marker-pin { background-color: #a4b0be; }
      
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
      
      .crime-popup h3 {
        font-weight: bold;
        margin-bottom: 5px;
      }
      .crime-popup p {
        margin: 5px 0;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // If this is a crime map with no reports
  if (crimeReports.length === 0 && !onLocationSelect) {
    return (
      <div ref={mapContainerRef} className="w-full h-full">
        <NoReportsMessage 
          title="No Crime Reports" 
          description="There are no crime reports to display on the map."
        />
      </div>
    );
  }

  return <div ref={mapContainerRef} className="w-full h-full" />;
};

export default FallbackLeafletMap;
